import { useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { FileText, Download, Loader2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subWeeks } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Reports = () => {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reportType, setReportType] = useState(null);

  const generateReport = async (type) => {
    setLoading(true);
    setReportType(type);
    try {
      let start, end, weekdaysOnly;
      const now = new Date();
      if (type === 'weekly') {
        const lastWeek = subWeeks(now, 1);
        start = startOfWeek(now, { weekStartsOn: 1 });
        end = endOfWeek(now, { weekStartsOn: 1 });
        end.setDate(end.getDate() - 2);
        weekdaysOnly = true;
      } else {
        start = startOfMonth(now);
        end = endOfMonth(now);
        weekdaysOnly = false;
      }

      const token = sessionStorage.getItem('auth_token');
      const res = await axios.get(`${API}/reports/team-performance`, {
        params: {
          start_date: format(start, 'yyyy-MM-dd'),
          end_date: format(end, 'yyyy-MM-dd'),
          weekdays_only: weekdaysOnly
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      setReportData(res.data);
      toast.success('Report generated');
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error('Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const exportCSV = () => {
    if (!reportData) return;
    const headers = ['Employee', 'Yet to Start', 'In Progress', 'On Hold', 'Done', 'Overdue', 'Clients'];
    const rows = reportData.members.map(m => [
      m.memberName, m.yetToStart, m.inProgress, m.onHold, m.done, m.overdue, m.clients.join('; ')
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${reportType}-report-${reportData.startDate}-to-${reportData.endDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2 }}
      className="p-8 space-y-6"
      data-testid="reports-page"
    >
      <div>
        <h1 className="text-2xl sm:text-4xl font-bold tracking-tight">Reports</h1>
        <p className="text-muted-foreground mt-1">Generate weekly and monthly team performance reports</p>
      </div>

      <Card className="p-6 flex flex-wrap items-center gap-4">
        <Button
          onClick={() => generateReport('weekly')}
          disabled={loading}
          data-testid="generate-weekly-btn"
        >
          {loading && reportType === 'weekly' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />}
          Generate Weekly Report
        </Button>
        <Button
          onClick={() => generateReport('monthly')}
          disabled={loading}
          variant="outline"
          data-testid="generate-monthly-btn"
        >
          {loading && reportType === 'monthly' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />}
          Generate Monthly Report
        </Button>
        {reportData && (
          <Button onClick={exportCSV} variant="outline" data-testid="export-csv-btn">
            <Download className="h-4 w-4 mr-2" />
            Export as Excel (CSV)
          </Button>
        )}
      </Card>

      {reportData && (
        <Card className="p-6">
          <div className="mb-4">
            <h2 className="text-lg font-semibold">
              {reportType === 'weekly' ? 'Weekly' : 'Monthly'} Report: {reportData.startDate} to {reportData.endDate}
            </h2>
            <p className="text-xs text-muted-foreground">Generated {format(new Date(reportData.generatedAt), 'MMM dd, yyyy h:mm a')}</p>
          </div>
          <div className="overflow-x-auto hidden md:block">
            <table className="w-full">
              <thead className="bg-muted/50 border-b border-border">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-mono uppercase tracking-wider text-muted-foreground">Employee</th>
                  <th className="px-4 py-3 text-center text-xs font-mono uppercase tracking-wider text-muted-foreground">Yet to Start</th>
                  <th className="px-4 py-3 text-center text-xs font-mono uppercase tracking-wider text-muted-foreground">In Progress</th>
                  <th className="px-4 py-3 text-center text-xs font-mono uppercase tracking-wider text-muted-foreground">On Hold</th>
                  <th className="px-4 py-3 text-center text-xs font-mono uppercase tracking-wider text-muted-foreground">Done</th>
                  <th className="px-4 py-3 text-center text-xs font-mono uppercase tracking-wider text-muted-foreground">Overdue</th>
                  <th className="px-4 py-3 text-left text-xs font-mono uppercase tracking-wider text-muted-foreground">Clients</th>
                </tr>
              </thead>
              <tbody>
                {reportData.members.map((m) => (
                  <tr key={m.memberId} className="border-b border-border/50">
                    <td className="px-4 py-3 text-sm font-medium">{m.memberName}</td>
                    <td className="px-4 py-3 text-sm text-center">{m.yetToStart}</td>
                    <td className="px-4 py-3 text-sm text-center">{m.inProgress}</td>
                    <td className="px-4 py-3 text-sm text-center">{m.onHold}</td>
                    <td className="px-4 py-3 text-sm text-center text-green-700 font-medium">{m.done}</td>
                    <td className={`px-4 py-3 text-sm text-center ${m.overdue > 0 ? 'text-red-600 font-semibold' : ''}`}>{m.overdue}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{m.clients.join(', ') || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Mobile Card View */}
          <div className="md:hidden space-y-3">
            {reportData.members.map((m) => (
              <div key={m.memberId} className="p-4 rounded-lg border border-border/60">
                <p className="font-semibold text-sm mb-2">{m.memberName}</p>
                <div className="grid grid-cols-2 gap-2 text-xs mb-2">
                  <p>Yet to Start: <span className="font-medium">{m.yetToStart}</span></p>
                  <p>In Progress: <span className="font-medium">{m.inProgress}</span></p>
                  <p>On Hold: <span className="font-medium">{m.onHold}</span></p>
                  <p>Done: <span className="font-medium text-green-700">{m.done}</span></p>
                  <p className={m.overdue > 0 ? 'text-red-600 font-semibold' : ''}>Overdue: {m.overdue}</p>
                </div>
                <p className="text-xs text-muted-foreground">Clients: {m.clients.join(', ') || '-'}</p>
              </div>
            ))}
          </div>
        </Card>
      )}
    </motion.div>
  );
};

export default Reports;
